import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
/**
 * Wraps the Google Maps PlaceAutocompleteElement web component (new API, not legacy widget).
 * Restricted to Morocco (includedRegionCodes: ['ma']).
 *
 * Styling note: PlaceAutocompleteElement uses shadow DOM.
 * The host element inherits font-family and color from the parent.
 * Inner parts are not fully exposed in v1 - a TODO comment marks this for future ::part() theming.
 * For now the web component uses its default look; the container div inherits brand styles.
 *
 * TODO: Use gmp-place-autocomplete::part(input) CSS when stable for brand-aligned styling.
 */
export function PlaceAutocompleteInput({ apiKey, onPlaceSelected, placeholder, className, }) {
    const containerRef = useRef(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!apiKey) {
            setError('missing-api-key');
            return;
        }
        const loader = new Loader({ apiKey, version: 'weekly', libraries: ['places'] });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let element;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loader.importLibrary('places').then((places) => {
            element = new places.PlaceAutocompleteElement();
            element.includedRegionCodes = ['ma'];
            if (placeholder) {
                element.placeholder = placeholder;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            element.addEventListener('gmp-select', async (e) => {
                try {
                    const prediction = e.placePrediction;
                    const place = prediction.toPlace();
                    await place.fetchFields({
                        fields: ['displayName', 'formattedAddress', 'location', 'id'],
                    });
                    const lat = typeof place.location?.lat === 'function'
                        ? place.location.lat()
                        : place.location?.lat;
                    const lng = typeof place.location?.lng === 'function'
                        ? place.location.lng()
                        : place.location?.lng;
                    onPlaceSelected({
                        placeId: place.id ?? prediction.placeId ?? '',
                        formattedAddress: place.formattedAddress ?? '',
                        lat,
                        lng,
                    });
                }
                catch (err) {
                    console.error('[PlaceAutocompleteInput] gmp-select error', err);
                }
            });
            if (containerRef.current) {
                containerRef.current.replaceChildren(element);
            }
        }).catch((err) => {
            setError(err.message ?? 'places-load-error');
        });
        return () => {
            element?.remove?.();
        };
        // onPlaceSelected is excluded from deps intentionally - callers should memoize it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, placeholder]);
    if (error === 'missing-api-key') {
        return (_jsx("p", { className: "rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700", children: "Google Maps API key non configur\u00E9e. D\u00E9finissez VITE_GOOGLE_MAPS_API_KEY." }));
    }
    if (error) {
        return (_jsx("p", { className: "rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700", children: error }));
    }
    return (_jsx("div", { ref: containerRef, className: className, style: {
            /* Blend the web-component host into the brand form style */
            fontFamily: 'inherit',
            fontSize: '1rem',
            lineHeight: 1.65,
        } }));
}
